import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { initializeTeamsApp, getTeamsToken } from "./store/actions/userActions";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Header from "./components/PageHeader";
import Sidebar from "./components/Sidenav";
import Content from "./components/Content";
import Dashboard from "./pages/dashboard/DashboardComponent";
import PortalContent from "./pages/portal-content/PortalContent";
import GeneralManage from "./pages/general/manage/GeneralManage";
import UserManageComponent from "./pages/user/manage/UserManageComponent";
import { initializeIcons } from "@fluentui/react";
import { ToastContainer } from "react-toastify";
import { useLoadingBar } from "react-top-loading-bar";
import { setLoadingBar, setAuthRefreshCallback } from "./services/httpClient";
import NotificationsPage from "./pages/notifications/NotificationsPage";
import { setAuthRefreshHandler } from "./utils/errorHandler";
import "react-toastify/dist/ReactToastify.css";
import "./index.css";
import "./dark-theme.css";

function App() {
  const dispatch = useDispatch();
  const { tokenError } = useSelector((state) => state.user);
  const { data: portalsData } = useSelector(state => state.api.portals);

  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const handleMenuClick = () => setIsSideNavOpen(true);
  const handleCloseSideNav = () => setIsSideNavOpen(false);

  const { start, complete } = useLoadingBar({
    color: "#6264A7",
    height: 3,
    className: "custom-loading-bar",
  });

  initializeIcons();

  useEffect(() => {
    dispatch(initializeTeamsApp());
    
    // Create auth refresh handler that triggers Teams token flow
    const authRefresh = async () => {
      return await getTeamsToken(dispatch);
    };

    // Set up auth refresh callback for interceptor (on token expiration)
    setAuthRefreshCallback(authRefresh);

    // Set up auth refresh handler for error handler (on 401 errors)
    setAuthRefreshHandler(authRefresh);
  }, [dispatch]);

  useEffect(() => {
    const customStart = () => {
      setIsLoading(true);
      start();
    };

    const customComplete = () => {
      setIsLoading(false);
      complete();
    };

    setLoadingBar({ start: customStart, complete: customComplete });
  }, [start, complete]);

  // Show error card if token initialization fails
  if (tokenError && !tokenError.includes('development mode')) {
    return (
      <div className="api-error-container">
        <div className="api-error-card">
          <div className="api-error-title">
            Access Denied
          </div>
          <div className="api-error-message">
            Your account does not have the required permissions to access this page.
            For further assistance, please reach out to the administrator at
          </div>
          <div className="api-error-contact">
            admin@octavehi.com
          </div>
        </div>
      </div>
    );
  }
  return (
    <Router>
      {isLoading && <div className="loading-overlay"></div>}
      <div className="layout-container">
        <Sidebar isOpen={isSideNavOpen} onClose={handleCloseSideNav} onCollapsedChange={setIsSidebarCollapsed} />
        <div className={`main-wrapper ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <Header onMenuClick={handleMenuClick} />
          <Content>
            <Routes>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/general-manage" element={<GeneralManage />} />
              <Route path="/user-manage" element={<UserManageComponent />} />
              <Route path="/portal/:portalId" element={<PortalContent />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/" element={
                portalsData && portalsData.length > 0
                  ? <Navigate to={`/portal/${portalsData[0].id}`} replace />
                  : <Navigate to="/general-manage" replace />
              } />
            </Routes>
          </Content>
        </div>
      </div>
      <ToastContainer />
    </Router>
  );
}

export default App;
